import { useCallback, useEffect, useRef, useState } from 'react';
import { adminAPI } from '../../../services/api';
import { CustomerIntelligenceData, normalizeCustomerPhone } from './customerIntelligence';
export { normalizeCustomerPhone } from './customerIntelligence';

export function useCustomerIntelligence(phone: string, isOpen: boolean) {
  const [customerHistory, setHistory] = useState<CustomerIntelligenceData | null>(null);
  const [customerHistoryLoading, setLoading] = useState(false);
  const [customerHistoryError, setError] = useState('');
  const sequence = useRef(0);
  const invalidateRequests = useCallback(() => ++sequence.current, []);
  const normalized = normalizeCustomerPhone(phone);
  const loadCustomerHistory = useCallback(async (value: string, refresh = false) => {
    const request = invalidateRequests();
    setHistory(null);
    setError('');
    setLoading(false);
    const clean = normalizeCustomerPhone(value);
    if (!/^01[3-9][0-9]{8}$/.test(clean)) return;
    setLoading(true);
    try {
      const response = await adminAPI.getCustomerSteadfastHistory(clean, refresh);
      if (sequence.current !== request) return;
      if (!response.data?.success) throw new Error(response.data?.error || 'Invalid customer history response.');
      setHistory(response.data);
    } catch (error: any) {
      if (sequence.current === request) {
        setError(error?.response?.data?.error || error?.message || 'Unable to check courier history. Please retry.');
      }
    } finally {
      if (sequence.current === request) setLoading(false);
    }
  }, [invalidateRequests]);

  useEffect(() => {
    invalidateRequests();
    setHistory(null);
    setError('');
    setLoading(false);
    if (!isOpen) return;
    const timer = setTimeout(() => { void loadCustomerHistory(normalized); }, 350);
    return () => { clearTimeout(timer); invalidateRequests(); };
  }, [normalized, isOpen, loadCustomerHistory, invalidateRequests]);

  return { customerHistory, customerHistoryLoading, customerHistoryError, loadCustomerHistory };
}
